/**
 * AIImportDecisionStep
 * Shown after Review. The user has seen what Claude extracted; here they decide
 * whether to turn it into a deal or just enrich the database. The choice sets
 * plan.import_mode, which is persisted immediately before Apply — the
 * ai-import-apply Edge Function gates deal creation on that field.
 */

import { useState, type ElementType } from 'react';
import { Briefcase, Database, Sparkles, Loader2, AlertTriangle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { useUpdateImportPlan, useApplyAIImportJob } from '@/hooks/useAIImport';
import { useAuthStore } from '@/stores/authStore';
import { useMicrosoftAuth } from '@/hooks/useMicrosoftAuth';
import { AnalysisPanel } from '../AnalysisPanel';
import { PropertyStarCell } from '../shared/PropertyStarCell';
import {
  planPropertyStarKey,
  recomputeSavedRatings,
  type CandidateRatingMap,
} from '../candidateRatings';
import type { AIImportMode, AIImportPlan } from '@/types/ai-import';

interface AIImportDecisionStepProps {
  jobId: string;
  /** The exact plan the user reviewed (passed from the Review step). */
  plan: AIImportPlan;
  /** Held candidate star previews, keyed by `planPropertyStarKey`. */
  candidateRatings?: CandidateRatingMap;
  /** Whether the user opted into star-rating analysis (drives recompute-on-save). */
  runStarRating?: boolean;
  onApplied: () => void;
  onBack: () => void;
  onClose: () => void;
}

function ChoiceCard({
  icon: Icon,
  title,
  description,
  bullets,
  actionLabel,
  onClick,
  accent,
  disabled,
  loading,
}: {
  icon: ElementType;
  title: string;
  description: string;
  bullets: string[];
  /** Explicit commit verb shown in the card footer so the click reads as "apply". */
  actionLabel: string;
  onClick: () => void;
  accent: 'primary' | 'emerald';
  disabled: boolean;
  loading: boolean;
}) {
  const ring = accent === 'primary' ? 'hover:border-primary-400' : 'hover:border-emerald-400';
  const iconBg =
    accent === 'primary'
      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
      : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
  const actionColor =
    accent === 'primary' ? 'text-primary-700 dark:text-primary-300' : 'text-emerald-700 dark:text-emerald-300';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`group text-left w-full rounded-xl border border-gray-200 p-5 transition-colors bg-white dark:border-gray-700 dark:bg-gray-900 ${ring} hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      <div className="flex items-start gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
          <ul className="mt-3 space-y-1">
            {bullets.map((b) => (
              <li key={b} className="text-xs text-gray-600 flex items-start gap-1.5 dark:text-gray-400">
                <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-gray-400" />
                {b}
              </li>
            ))}
          </ul>
          {/* Commit verb (not a ChevronRight "next" affordance) — a single click
              here persists the plan and applies the job. */}
          <div className={`mt-4 flex items-center gap-1.5 text-sm font-semibold ${actionColor}`}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
            {loading ? 'Applying…' : actionLabel}
          </div>
        </div>
      </div>
    </button>
  );
}

export function AIImportDecisionStep({
  jobId,
  plan,
  candidateRatings = {},
  runStarRating = false,
  onApplied,
  onBack,
  onClose,
}: AIImportDecisionStepProps) {
  const updatePlan = useUpdateImportPlan();
  const applyJob = useApplyAIImportJob();
  const queryClient = useQueryClient();
  const { user, profile } = useAuthStore();
  const { user: msUser } = useMicrosoftAuth();

  const [applyError, setApplyError] = useState<string | null>(null);
  const [pendingMode, setPendingMode] = useState<AIImportMode | null>(null);

  const currentUserName =
    profile?.full_name || msUser?.name || user?.email || msUser?.email || 'Unknown';
  const currentEmail = user?.email || msUser?.email || null;

  const createCount = plan.properties.filter((p) => p.action === 'create').length;
  const updateCount = plan.properties.filter((p) => p.action === 'update').length;
  const compCount = (plan.valuations?.length ?? 0) + (plan.rent_comps?.length ?? 0);
  const contactCount = plan.contacts?.length ?? 0;
  const dealName = plan.deal?.name || 'New deal';

  const busy = updatePlan.isPending || applyJob.isPending;

  const handleChoose = async (mode: AIImportMode) => {
    if (busy) return;
    setApplyError(null);
    setPendingMode(mode);
    try {
      await updatePlan.mutateAsync({ id: jobId, summary: { ...plan, import_mode: mode } });
      const result = await applyJob.mutateAsync({
        id: jobId,
        applied_by: currentUserName,
        applied_by_email: currentEmail,
      });
      // Persist the real rating by id through the trusted path for every saved
      // property (never the client-side preview). Fire-and-forget — the ratings
      // land while the user is on the confirm screen.
      if (runStarRating) {
        recomputeSavedRatings(
          [...(result.property_ids ?? []), ...(result.updated_property_ids ?? [])],
          queryClient,
        );
      }
      onApplied();
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : String(err));
      setPendingMode(null);
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
        <Sparkles className="h-4 w-4 text-primary-600 dark:text-primary-400" />
        Analysis complete. What would you like to do with these files?
      </div>

      {/* What was found — one-line context, kept tight above the actions */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
        <span className="font-medium text-gray-900 dark:text-gray-100">{dealName}</span>
        <span className="text-gray-500 dark:text-gray-400">
          {' · '}
          {createCount} new / {updateCount} updated propert{createCount + updateCount === 1 ? 'y' : 'ies'}
          {compCount > 0 ? ` · ${compCount} comp${compCount === 1 ? '' : 's'}` : ''}
          {contactCount > 0 ? ` · ${contactCount} contact${contactCount === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {/* ACTIONS FIRST — the whole point of this step. These sit above the
          analysis narrative so the user can decide without scrolling past it. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <ChoiceCard
          icon={Briefcase}
          accent="primary"
          title="Create a deal"
          description="Turn the files into a new deal, ready to underwrite."
          bullets={[
            'Creates a deal with all properties linked',
            'Builds a base financial model',
            'Captures sales/refi comps + market rents',
            'Seeds a diligence checklist',
          ]}
          actionLabel="Apply & create deal"
          onClick={() => handleChoose('deal')}
          disabled={busy}
          loading={pendingMode === 'deal'}
        />
        <ChoiceCard
          icon={Database}
          accent="emerald"
          title="Add to database only"
          description="Enrich Property Lists and Research — no deal."
          bullets={[
            'Adds new properties / updates existing',
            'Addresses, lot/unit counts, RV vs MH, ownership',
            'Captures sales/refi comps + market rents',
            'No deal or financial model created',
          ]}
          actionLabel="Apply to database"
          onClick={() => handleChoose('database')}
          disabled={busy}
          loading={pendingMode === 'database'}
        />
      </div>

      {applyError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>{applyError}</div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} disabled={busy}>
          Back to review
        </Button>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Save &amp; close
        </Button>
      </div>

      {/* DETAILS BELOW — the analysis the report email carries (metrics, returns
          sensitivity, common-size vs our averages, narrative) and quality
          ratings. Reference for the decision above, not a gate to reach it. */}
      <div className="border-t border-gray-100 pt-4 space-y-5 dark:border-gray-700">
        <AnalysisPanel jobId={jobId} />

        {runStarRating && plan.properties.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <Sparkles className="h-3.5 w-3.5 text-accent-500" />
              Quality ratings
            </div>
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {plan.properties.map((p, idx) => (
                <li key={idx} className="flex items-center justify-between gap-3 py-1.5">
                  <span className="min-w-0 truncate text-sm text-gray-700 dark:text-gray-300">
                    {(p.fields_to_apply.name as string) || '(unnamed property)'}
                  </span>
                  <PropertyStarCell
                    property={p}
                    candidate={candidateRatings[planPropertyStarKey(idx)]}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
